import { Box, IconButton } from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import { RiDeleteBinLine, RiFileEditLine } from "react-icons/ri";
import { MdWarningAmber } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import CTable from "../../../components/Table/table";
import { brandPalette } from "../../../constants/brandPalette";
import {
  deleteAssistantRequest,
  suspendAssistantRequest,
} from "../../../modules/admin-modules/assistants/Actions";
import { openCreateAssistant } from "../../../modules/admin-modules/models/Actions";
import { useDeleteConfirm } from "../../../hooks/useDeleteConfirm";

const C = {
  primary: brandPalette.primary,
  secondary: brandPalette.accentDark,
  border: brandPalette.stone,
};

const iconBtnProps = {
  variant: "ghost",
  border: "0",
  size: "sm",
  color: C.primary,
  _hover: { bg: "rgba(91, 13, 22, 0.08)", color: C.secondary },
};

const AssistantsTable = ({ setAdd, setSelectedData, searchTerm = "" }) => {
  const assistants = useSelector((state) => state.assistants.assistants);
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.assistants.isLoading);
  const [, setPage] = useState(0);
  const [size, setPerPage] = useState(10);
  const [pageNumber, setPageNumber] = useState(0);
  const { confirmDelete, deleteConfirmModal } = useDeleteConfirm({
    title: "Delete assistant?",
  });
  const {
    confirmDelete: confirmSuspend,
    deleteConfirmModal: suspendConfirmModal,
  } = useDeleteConfirm({
    title: "Suspend assistant?",
    confirmLabel: "Suspend",
  });

  const list = useMemo(() => {
    const raw = Array.isArray(assistants) ? assistants : [];
    const q = (searchTerm || "").trim().toLowerCase();
    if (!q) return raw;
    return raw.filter(
      (a) =>
        a?.name?.toLowerCase().includes(q) ||
        String(a?.phone ?? "")
          .toLowerCase()
          .includes(q)
    );
  }, [assistants, searchTerm]);

  useEffect(() => {
    setPageNumber(0);
  }, [searchTerm]);

  const viewData = (row) => {
    const plain = list.find((a) => a.id === row?.id) ?? row;
    setSelectedData(plain);
    setAdd(false);
    dispatch(openCreateAssistant());
  };

  const Data = list.map((data) => ({
    ...data,
    id: data?.id,
    name: data?.name,
    phone: data?.phone,
    edit: (
      <IconButton
        {...iconBtnProps}
        aria-label="Edit assistant"
        fontSize="20px"
        icon={<RiFileEditLine />}
        onClick={(e) => {
          e.stopPropagation();
          setSelectedData(data);
          setAdd(false);
          dispatch(openCreateAssistant());
        }}
      />
    ),
    suspend: (
      <IconButton
        {...iconBtnProps}
        aria-label="Suspend assistant"
        fontSize="20px"
        icon={
          <MdWarningAmber
            color={data?.deleted_at == null ? "#c53030" : "#2f855a"}
          />
        }
        onClick={(e) => {
          e.stopPropagation();
          confirmSuspend({
            name: data?.name,
            onConfirm: () => dispatch(suspendAssistantRequest(data?.id, "")),
          });
        }}
      />
    ),
    delete: (
      <IconButton
        {...iconBtnProps}
        aria-label="Delete assistant"
        fontSize="20px"
        icon={<RiDeleteBinLine color="#9b2c2c" />}
        onClick={(e) => {
          e.stopPropagation();
          confirmDelete({
            name: data?.name,
            onConfirm: () => dispatch(deleteAssistantRequest(data?.id)),
          });
        }}
      />
    ),
  }));

  const columns = [
    { Header: "Name", accessor: "name" },
    { Header: "Phone", accessor: "phone" },
    { Header: "Suspend", accessor: "suspend" },
    { Header: "Edit", accessor: "edit" },
    { Header: "Delete", accessor: "delete" },
  ];

  const totalPage = Math.max(1, Math.ceil(list.length / size));

  return (
    <>
    <Box px={{ base: 4, md: 8 }} pb={10} w="100%">
      <Box
        bg="var(--surface-raised)"
        border="1px solid"
        borderColor={C.border}
        borderRadius="16px"
        overflow="hidden"
        boxShadow="sm"
      >
        <CTable
          selectedData={viewData}
          Columns={columns}
          Data={Data}
          path="openModel"
          Title=""
          subTitle=""
          btnTitle=""
          placeHolder=""
          noSearchBar
          noFilter
          footerBtnTitle
          filterList
          addButton
          addButtonTitle=""
          setPage={setPage}
          setPerPage={setPerPage}
          currentpage={pageNumber}
          setPageNumber={setPageNumber}
          noSecondSearchBar
          isLoading={isLoading}
          perPage={size}
          totalPage={totalPage}
          wrapperProps={{ maxH: "unset", overflow: "auto" }}
          tableProps={{ bg: "transparent", borderRadius: "0", mb: 0 }}
          theadProps={{ bg: "var(--surface-muted)", color: "var(--text-primary)" }}
        />
      </Box>
    </Box>
    {deleteConfirmModal}
    {suspendConfirmModal}
    </>
  );
};

export default AssistantsTable;
